import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { useCreateKnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Globe,
  BookOpen,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { KnowledgeItem } from "@/services/fileService";

// URL validation function
const isValidUrl = (string: string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const websiteTrainingSchema = z.object({
  url: z
    .string()
    .min(1, "URL is required")
    .refine((url) => isValidUrl(url), {
      message: "Please enter a valid URL (must start with http:// or https://)",
    }),
  pages: z.number().min(1).max(100).default(15),
});

interface WebsiteTrainingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (item?: KnowledgeItem) => void;
}

type CrawlStage =
  | "INITIATING"
  | "CRAWLING"
  | "PROCESSING"
  | "FINALIZING"
  | "COMPLETED"
  | null;

const stageMessages: Record<
  Exclude<CrawlStage, null>,
  { message: string; progress: number }
> = {
  INITIATING: { message: "🔍 Analyzing website...", progress: 25 },
  CRAWLING: { message: "📄 Starting content extraction...", progress: 75 },
  PROCESSING: { message: "🧠 Preparing for AI search...", progress: 75 },
  FINALIZING: { message: "✅ Finalizing...", progress: 90 },
  COMPLETED: {
    message:
      "✅ Website content added! Your agents can now answer questions about your business.",
    progress: 100,
  },
};

const WebsiteTrainingModal = ({
  open,
  onOpenChange,
  onSuccess,
}: WebsiteTrainingModalProps) => {
  const { createItem, isLoading } = useCreateKnowledgeItem();
  const { organization } = useAuth();
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlStage, setCrawlStage] = useState<CrawlStage>(null);
  const [crawlError, setCrawlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedItemData, setSavedItemData] = useState<{
    id: string;
    url: string;
    title: string;
    description: string | null;
    pages: number;
  } | null>(null);

  const form = useForm<z.infer<typeof websiteTrainingSchema>>({
    resolver: zodResolver(websiteTrainingSchema),
    defaultValues: {
      url: "",
      pages: 10,
    },
  });

  // Update form with organization website when organization data loads
  useEffect(() => {
    if (organization) {
      // Get website from organization.website field or contact_info.website
      const contactInfo = organization.contact_info as {
        website?: string;
      } | null;
      const websiteUrl = organization.website || contactInfo?.website || "";

      if (websiteUrl) {
        form.setValue("url", websiteUrl);
      }
    }
  }, [organization, form]);

  // Clear form errors when form values change
  const clearFormError = () => {
    if (formError) {
      setFormError(null);
    }
  };

  const processLinkForAI = async (linkData: typeof savedItemData) => {
    if (!linkData || !organization) return;

    setIsCrawling(true);
    setCrawlError(null);
    setCrawlStage("INITIATING");

    try {
      setCrawlStage("CRAWLING");

      // Get the user session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firecrawl-crawl`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            url: linkData.url,
            linkId: linkData.id,
            title: linkData.title,
            description: linkData.description,
            organizationId: organization.id,
            agentId: null, // Organization level
            limit: linkData.pages || 10,
            scrapeOptions: {
              timeout: 30000,
              formats: ["markdown"],
              removeBase64Images: true,
              onlyMainContent: true,
              blockAds: true,
              waitFor: 2000,
              mobile: false,
            },
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Crawl failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log("Crawl response:", result);

      setCrawlStage("PROCESSING");

      // Simulate processing time
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setCrawlStage("FINALIZING");
      await new Promise((resolve) => setTimeout(resolve, 500));

      setCrawlStage("COMPLETED");

      // Wait a bit to show the completion message
      setTimeout(() => {
        setIsCrawling(false);
        if (onSuccess) {
          onSuccess();
        } else {
          onOpenChange(false);
        }
      }, 2000);
    } catch (error: any) {
      console.error("Crawl processing failed:", error);
      setCrawlError(
        error.message ||
          "An unknown error occurred. Please try again or contact support.",
      );
      setCrawlStage(null);
      setIsCrawling(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof websiteTrainingSchema>) => {
    if (!organization?.id) {
      setFormError("No organization found");
      return;
    }

    setFormError(null);

    try {
      // Create the knowledge item first
      const result = await createItem({
        title: `${organization.name}'s Website`,
        description:
          "You can use our website to answer user's questions about our business",
        type: "link",
        url: values.url,
        agent_id: null, // Organization level by default
      });

      if (result) {
        const itemData = {
          id: result.id,
          url: values.url,
          title: `${organization.name}'s Website`,
          description:
            "You can use our website to answer user's questions about our business",
          pages: values.pages,
        };

        setSavedItemData(itemData);
        await processLinkForAI(itemData);
      } else {
        throw new Error("Failed to create knowledge item");
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      setFormError(
        error.message ||
          "An unknown error occurred. Please try again or contact support.",
      );
    }
  };

  const handleRetry = () => {
    if (savedItemData) {
      processLinkForAI(savedItemData);
    }
  };

  const currentStage = crawlStage ? stageMessages[crawlStage] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Add your website content to your knowledge base
          </DialogTitle>
          <DialogDescription>
            Let your AI employees scan your website to learn about your business
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com"
                      {...field}
                      disabled={isLoading || isCrawling}
                      onChange={(e) => {
                        field.onChange(e);
                        clearFormError();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pages"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pages to Crawl: {field.value}</FormLabel>
                  <FormControl>
                    <div className="px-2">
                      <Slider
                        min={1}
                        max={100}
                        step={1}
                        value={[field.value]}
                        onValueChange={(value) => field.onChange(value[0])}
                        disabled={isLoading || isCrawling}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>1 page</span>
                        <span>100 pages</span>
                      </div>
                    </div>
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    More pages = more content but longer crawling time. Most
                    websites need 1-10 pages.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Crawling Progress */}
            {isCrawling && currentStage && (
              <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 animate-pulse text-primary" />
                  <span className="text-sm font-medium">
                    {currentStage.message}
                  </span>
                </div>
                <Progress value={currentStage.progress} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Crawling {form.getValues("pages")} page(s). This usually takes
                  30-90 seconds per page.
                </p>
              </div>
            )}

            {/* Success Message */}
            {crawlStage === "COMPLETED" && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Success!</AlertTitle>
                <AlertDescription className="text-green-700">
                  Website content has been crawled and indexed for AI search.
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {crawlError && !isCrawling && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Failed to crawl website</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>{crawlError}</p>
                  {savedItemData && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRetry}
                      className="mt-2"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry Crawl
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                disabled={isLoading || isCrawling}
                className="w-full bg-brand-yellow hover:bg-yellow-400 text-white"
              >
                {isLoading || isCrawling
                  ? "Training in progress..."
                  : "🚀 Launch website training"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default WebsiteTrainingModal;
